import type { SaunaSessionSummary } from '@jums-dashboard/shared';
import { useMemo, useState } from 'react';

import Badge from '@/components/Badge/Badge';
import { cn } from '@/lib/cn';

import { filterSessions, formatDateTime, formatHeatingRate, formatHeatingTime, formatTempC, type SessionFilter } from './sessionHistory';

const FILTERS: { value: SessionFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
];

const IN_PROGRESS_STATUSES = new Set<SaunaSessionSummary['status']>(['warming', 'ready', 'inUse']);

export interface SaunaSessionListProps {
  sessions: readonly SaunaSessionSummary[];
  truncated: boolean;
}

export default function SaunaSessionList({ sessions, truncated }: SaunaSessionListProps) {
  const [filter, setFilter] = useState<SessionFilter>('all');
  const visible = useMemo(() => filterSessions(sessions, filter), [sessions, filter]);

  return (
    <div className="flex flex-col gap-2">
      <div role="group" aria-label="Filter sessions" className="inline-flex self-start rounded-lg border border-(--border-subtle) p-0.5">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            aria-pressed={filter === f.value}
            onClick={() => setFilter(f.value)}
            className={cn(
              'rounded-md px-3 py-1 text-xs font-medium transition-colors',
              'focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:outline-none',
              filter === f.value ? 'bg-(--bg-surface-strong) text-(--text-primary)' : 'text-(--text-tertiary) hover:text-(--text-primary)',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-(--text-tertiary)">{filter === 'completed' ? 'No completed sessions yet.' : 'No sessions yet.'}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-(--border-subtle) rounded-md border border-(--border-subtle)">
          {visible.map((s) => (
            <SessionRow key={s.sessionId} session={s} />
          ))}
        </ul>
      )}

      {truncated && <p className="text-xs text-(--text-tertiary)">Showing the most recent {sessions.length} sessions.</p>}
    </div>
  );
}

function SessionRow({ session: s }: { session: SaunaSessionSummary }) {
  return (
    <li className="flex flex-col gap-1 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-(--text-primary)">{formatDateTime(s.startedAt)}</span>
        {s.status === 'aborted' && <Badge tone="danger">Aborted</Badge>}
        {IN_PROGRESS_STATUSES.has(s.status) && <Badge tone="info">In progress</Badge>}
        {s.completed && s.heatingRateLowConfidence && (
          <span title={`Only ${s.heatingRateIntervalCount} sample intervals, so the rate may be unreliable.`}>
            <Badge tone="warning">Low confidence</Badge>
          </span>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-(--text-secondary) sm:grid-cols-4">
        <Stat label="Start" value={formatTempC(s.startTemperatureC)} />
        <Stat label="Max" value={formatTempC(s.maxTemperatureC)} />
        <Stat
          label="Heating time"
          value={s.completed ? formatHeatingTime(s.heatingTimeMinutes) : '—'}
          approximate={s.completed && s.targetReachedAtApproximate}
        />
        <Stat label="Heating rate" value={s.completed ? formatHeatingRate(s.heatingRateCPerMin) : '—'} />
      </dl>
    </li>
  );
}

function Stat({ label, value, approximate = false }: { label: string; value: string; approximate?: boolean }) {
  return (
    <div className="flex gap-1">
      <dt className="text-(--text-tertiary)">{label}</dt>
      <dd
        className="font-medium text-(--text-primary) tabular-nums"
        title={approximate ? 'Approximate: estimated from when the sauna became ready.' : undefined}
      >
        {approximate ? `≈ ${value}` : value}
      </dd>
    </div>
  );
}
